import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { LeadData, Status } from "../../../src/types";
import ListboxComponent from "../../components/common/ListboxComponent";

const ConvertLeadSection: React.FC<{ lead: LeadData }> = ({ lead }) => {
  const [statuses, setStatuses] = useState<Status[]>([]);
const [selectedStatus, setSelectedStatus] = useState<Status | null>(null);

  const [loading, setLoading] = useState<boolean>(false);
  const [, setStatusLoading] = useState<boolean>(true);
  const [errors, setErrors] = useState<{ selectedStatus?: string }>({});

useEffect(() => {
  const fetchStatuses = async () => {
    const token = sessionStorage.getItem("access_token");
    if (!token) {
      toast.error("Authentication error! Please log in.");
      setStatusLoading(false);
      return;
    }

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/status/name/list`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

if (response.data.status === 200 && Array.isArray(response.data.data)) {
  const fetchedStatuses = response.data.data;
  setStatuses(fetchedStatuses);

  // Match by status_name instead of id
  const initialStatus = fetchedStatuses.find(
    (status: Status) => status.status_name === lead.status
  );

  setSelectedStatus(initialStatus ?? null);
}
 else {
        throw new Error("Invalid response structure");
      }
    } catch (error) {
      toast.error("Failed to fetch statuses.");
      console.error("Fetch statuses error:", error);
      setStatuses([]);
    } finally {
      setStatusLoading(false);
    }
  };

  fetchStatuses();
}, [lead.status]);



  const handleStatusChange = (status: Status | null) => {
    setSelectedStatus(status);
    setErrors({
      selectedStatus: status ? undefined : "Please select a status.",
    });
  };

  const handleConvertLead = async () => {
    const token = sessionStorage.getItem("access_token");
    if (!token) {
      toast.error("Authentication error! Please log in.");
      return;
    }

    if (!selectedStatus) {
      setErrors({ selectedStatus: "Please select a status." });
      toast.error("Please select a status.");
      return;
    }

    setErrors({});
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("lead_id", lead.lead_id);
      formData.append("lead_status", String(selectedStatus.id));

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/convert-lead`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.data.status === 200) {
        toast.success("Lead converted successfully!");
      } else {
        if (
          response.data?.status === 500 &&
          response.data?.message ===
          "Lead cannot be converted. No opportunities found."
        ) {
          toast.error("Lead cannot be converted. No opportunities found.");
        } else {
          toast.error(response.data?.message || "Failed to convert lead.");
        }
      }
    } catch (error) {
      toast.error("An unexpected error occurred while converting the lead.");
      console.error("Convert lead error:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="mt-6 bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-100 border border-gray-300 dark:border-gray-600 shadow-sm p-6 rounded-lg transition-colors duration-300 flex flex-col"
      style={{ fontFamily: "Lato, sans-serif", fontWeight: "normal" }}
    >
      <h3 className="text-lg font-semibold mb-4 text-gray-800 dark:text-white/90">Convert Lead</h3>

      {/* Grid layout for fields */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-20">
        {/* Opportunity Amount */}
        <div>
          <strong className="text-sm font-medium text-gray-500 dark:text-gray-400 block mb-1">
            Opportunity Amount
          </strong>
          <p className="text-sm text-black dark:text-white">
            {lead.opportunity_amount !== undefined && lead.opportunity_amount !== null
              ? `₹ ${Number(lead.opportunity_amount).toLocaleString("en-IN", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}`
              : "N/A"}
          </p>
        </div>

        {/* Status Listbox */}
        <div>
          <ListboxComponent
            value={selectedStatus}
            onChange={handleStatusChange}
            items={statuses}
            label="Status"
            error={errors.selectedStatus}
            displayKey="status_name"
            required
          />
        </div>
      </div>

      {/* Button aligned to bottom-right */}
      <div className="flex justify-end">
        <button
          onClick={handleConvertLead}
          disabled={loading}
          className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label="Convert lead"
        >
          {loading ? "Converting..." : "Convert Lead"}
        </button>
      </div>
    </div>
  );
};

export default ConvertLeadSection;